/**
 * ConditionBuilder — the optional show_if branching editor (blue box). One or
 * MORE conditions, combined with any (OR) / all (AND):
 *
 *   - one condition is stored flat as `{ question_key, operator, value }` —
 *     the legacy shape every pre-existing form carries and the only shape the
 *     WCR renderer understood before this;
 *   - two or more are stored as `{ match, conditions[] }`.
 *
 * `is one of` / `is none of` (the `in` / `not_in` operators) take a LIST, which
 * is how you branch on a multiple-choice answer: "show this when they picked A
 * or B or C". They match option values EXACTLY, unlike `contains`, which is a
 * substring test that would also match option_1 inside option_10.
 *
 * Calculation questions are excluded as sources — they resolve only at
 * completion, so branching on one is a dead branch the backend rejects at
 * publish.
 */
import { X, Plus, Trash2 } from 'lucide-react';
import {
  FormQuestionDef, FormShowIf, FormShowIfCondition, FormShowIfGroup,
  normalizeShowIf, denormalizeShowIf,
} from '../../types';
import { OPERATOR_LABELS, showIfOperatorsForType, isListOperator, numberInputProps } from './formMeta';

interface ConditionBuilderProps {
  condition: FormShowIf | null | undefined;
  /** Candidate source questions (caller excludes self + calculations). */
  questions: FormQuestionDef[];
  onChange: (condition: FormShowIf | null) => void;
}

const selectCls =
  'block w-full rounded-lg border border-primary-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-primary-700 dark:bg-gray-800 dark:text-gray-100';

const truncate = (s: string, n = 60) => (s.length > n ? `${s.slice(0, n)}…` : s);

/** A fresh condition against `q`, with an operator and a value its type accepts. */
function seedCondition(q: FormQuestionDef | undefined): FormShowIfCondition {
  const operator = showIfOperatorsForType(q?.type)[0] || 'equals';
  return {
    question_key: q?.key || '',
    operator,
    value: isListOperator(operator) ? [] : (q?.type === 'yes_no' ? 'yes' : ''),
  };
}

/** Keep a value shaped for its operator when the operator (or source) changes:
 * a list operator always carries an array, every other one a scalar. */
function coerceValue(operator: string, value: FormShowIfCondition['value']): FormShowIfCondition['value'] {
  if (isListOperator(operator)) {
    if (Array.isArray(value)) return value;
    return value === '' || value === undefined || value === null ? [] : [value];
  }
  if (Array.isArray(value)) return value.length > 0 ? value[0] : '';
  return value;
}

export default function ConditionBuilder({ condition, questions, onChange }: ConditionBuilderProps) {
  const group: FormShowIfGroup | null = normalizeShowIf(condition);
  const conditions = group?.conditions || [];
  const match = group?.match || 'any';

  const emit = (next: FormShowIfCondition[], nextMatch = match) =>
    onChange(denormalizeShowIf(next.length ? { match: nextMatch, conditions: next } : null));

  const updateAt = (i: number, patch: Partial<FormShowIfCondition>) =>
    emit(conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const changeSource = (i: number, key: string) => {
    const q = questions.find((x) => x.key === key);
    emit(conditions.map((c, j) => (j === i ? seedCondition(q) : c)));
  };

  const changeOperator = (i: number, operator: string) =>
    updateAt(i, { operator, value: coerceValue(operator, conditions[i].value) });

  const addCondition = () => {
    // Default a second+ condition to the SAME source question — branching on
    // "they picked A or B" is far more common than a cross-question rule, and
    // the author can repoint it in one click.
    const source = questions.find((q) => q.key === conditions[0]?.question_key) || questions[0];
    emit([...conditions, seedCondition(source)]);
  };

  const removeAt = (i: number) => emit(conditions.filter((_, j) => j !== i));

  const renderValueInput = (cond: FormShowIfCondition, i: number) => {
    const source = questions.find((q) => q.key === cond.question_key);
    const options = source?.options || [];
    const list = Array.isArray(cond.value) ? cond.value.map(String) : [];

    if (isListOperator(cond.operator)) {
      if (options.length > 0) {
        const toggle = (value: string) => updateAt(i, {
          value: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
        });
        return (
          <div className="rounded-lg border border-primary-300 bg-white p-2 dark:border-primary-700 dark:bg-gray-800">
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {options.map((o, oi) => (
                <label key={`${oi}:${o.value}`} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={list.includes(o.value)}
                    onChange={() => toggle(o.value)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  {o.label}
                </label>
              ))}
            </div>
            {list.length === 0 && (
              <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">Tick at least one answer.</p>
            )}
          </div>
        );
      }
      if (source?.type === 'yes_no') {
        const toggle = (value: string) => updateAt(i, {
          value: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
        });
        return (
          <div className="flex flex-wrap gap-4 rounded-lg border border-primary-300 bg-white px-3 py-2 dark:border-primary-700 dark:bg-gray-800">
            {['yes', 'no'].map((v) => (
              <label key={v} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={list.includes(v)}
                  onChange={() => toggle(v)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                />
                {v === 'yes' ? 'Yes' : 'No'}
              </label>
            ))}
          </div>
        );
      }
      // Free-text / numeric source: comma-separated list.
      return (
        <input
          type="text"
          value={list.join(', ')}
          onChange={(e) => updateAt(i, {
            value: e.target.value.split(',').map((v) => v.trim()).filter((v) => v !== ''),
          })}
          placeholder="Comma-separated values"
          className={selectCls}
        />
      );
    }

    const scalar = String(Array.isArray(cond.value) ? '' : cond.value ?? '');
    if (options.length > 0) {
      return (
        <select value={scalar} onChange={(e) => updateAt(i, { value: e.target.value })} className={selectCls}>
          <option value="">Select a value</option>
          {options.map((o, oi) => <option key={`${oi}:${o.value}`} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (source?.type === 'yes_no') {
      return (
        <select value={scalar} onChange={(e) => updateAt(i, { value: e.target.value })} className={selectCls}>
          <option value="">Select a value</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );
    }
    if (source?.type === 'number' || source?.type === 'scale') {
      return (
        <input
          type="number"
          {...numberInputProps}
          value={scalar}
          onChange={(e) => updateAt(i, { value: e.target.value === '' ? '' : Number(e.target.value) })}
          placeholder="Enter a number"
          className={selectCls}
        />
      );
    }
    return (
      <input type="text" value={scalar} onChange={(e) => updateAt(i, { value: e.target.value })} placeholder="Enter a value" className={selectCls} />
    );
  };

  if (questions.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Add another non-calculation question first to enable conditional display.</p>;
  }

  if (conditions.length === 0) {
    return (
      <button
        type="button"
        onClick={() => onChange(seedCondition(questions[0]))}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
      >
        <Plus className="h-4 w-4" /> Add condition
      </button>
    );
  }

  const multi = conditions.length > 1;

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">Show this question when</span>
          {multi && (
            <>
              <select
                value={match}
                onChange={(e) => emit(conditions, e.target.value === 'all' ? 'all' : 'any')}
                className={`${selectCls} w-auto py-1`}
              >
                <option value="any">any</option>
                <option value="all">all</option>
              </select>
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">of these are true</span>
            </>
          )}
        </div>
        <button type="button" onClick={() => onChange(null)} className="shrink-0 rounded p-1 text-primary-600 hover:bg-primary-100 dark:text-primary-300 dark:hover:bg-primary-900/40" aria-label="Remove condition">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3">
        {conditions.map((cond, i) => {
          const source = questions.find((q) => q.key === cond.question_key);
          return (
            <div key={i} className={i > 0 ? 'border-t border-primary-200 pt-3 dark:border-primary-800' : undefined}>
              {multi && (
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                  {i === 0 ? 'Condition 1' : `${match === 'all' ? 'and' : 'or'} condition ${i + 1}`}
                </p>
              )}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <select value={cond.question_key} onChange={(e) => changeSource(i, e.target.value)} className={selectCls}>
                    {!source && <option value={cond.question_key}>{cond.question_key} (missing question)</option>}
                    {questions.map((q) => (
                      <option key={q.key} value={q.key}>{truncate(q.label || q.key)}</option>
                    ))}
                  </select>
                  {multi && (
                    <button
                      type="button"
                      onClick={() => removeAt(i)}
                      className="mt-1 shrink-0 rounded p-1 text-primary-600 hover:bg-primary-100 dark:text-primary-300 dark:hover:bg-primary-900/40"
                      aria-label={`Remove condition ${i + 1}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-start gap-2">
                  <select
                    value={cond.operator}
                    onChange={(e) => changeOperator(i, e.target.value)}
                    className={`${selectCls} w-auto`}
                  >
                    {showIfOperatorsForType(source?.type).map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
                  </select>
                  <div className="min-w-[150px] flex-1">{renderValueInput(cond, i)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addCondition}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline dark:text-primary-300"
      >
        <Plus className="h-3.5 w-3.5" /> Add condition
      </button>
      {multi && (
        <p className="mt-2 text-xs text-primary-700/80 dark:text-primary-300/80">
          {match === 'any'
            ? 'Shown when AT LEAST ONE condition is true.'
            : 'Shown only when EVERY condition is true.'}
        </p>
      )}
    </div>
  );
}
